// Відео
export interface SteamMovie {
  id: number;
  name: string;
  thumbnail: string;
  webm?: {
    480?: string;
    max?: string;
  };
  mp4?: {
    480?: string;
    max?: string;
  };
  highlight?: boolean;
  dash_av1?: string;
  dash_h264?: string;
  hls_h264?: string;
}

// Пакетні групи
export interface SteamPackageGroup {
  name: string;
  title: string;
  description: string;
  selection_text: string;
  save_text: string;
  display_type: number;
  is_recurring_subscription?: string;
  subs?: Array<{
    packageid: number;
    percent_savings_text: string;
    percent_savings: number;
    option_text: string;
    option_description: string;
    can_get_free_license: string;
    is_free_license: boolean;
    price_in_cents_with_discount: number;
  }>;
}

// Категорії
export interface GameCategory {
  id: number;
  type: number;
  internal_name: string;
  display_name: string;
  description: string;
  image_url: string | null;
  computed: boolean | null;
  show_in_search: boolean | null;
  edit_url: string | null;
  edit_sort_order: number | null;
}

// Жанри
export interface SteamGenre {
  id: string;
  description: string;
}

// Рейтинги
export interface SteamRating {
  rating?: string;
  rating_generated?: string;
  descriptors?: string;
  required_age?: string;
  banned?: string;
  use_age_gate?: string;
}

// Досягнення
export interface SteamAchievements {
  total: number;
  highlighted?: Array<{
    name: string;
    path: string;
  }>;
}

// Рекомендації
export interface SteamRecommendations {
  total: number;
}

// Ціни Steam
export interface PriceOverview {
  currency: string;
  initial: number;
  final: number;
  discount_percent: number;
  initial_formatted?: string;
  final_formatted?: string;
}

// Підтримка гри
export interface SupportInfo {
  url?: string;
  email?: string;
}

// ID контентних дескрипторів Steam (для фільтрів по content_descriptors.ids)
export const CONTENT_DESCRIPTOR_IDS = {
  NudityOrSexualContent: 1,
  FrequentViolenceOrGore: 2,
  AdultOnlySexualContent: 3,
  GratuitousSexualContent: 4,
  AnyMatureContent: 5,
} as const;

export type ContentDescriptorId =
  (typeof CONTENT_DESCRIPTOR_IDS)[keyof typeof CONTENT_DESCRIPTOR_IDS];

export const CONTENT_DESCRIPTOR_LABELS: Record<ContentDescriptorId, string> = {
  1: "Nudity or Sexual Content",
  2: "Frequent Violence or Gore",
  3: "Adult Only Sexual Content",
  4: "Gratuitous Sexual Content",
  5: "Any Mature Content",
};

// Контентні дескриптори
export interface ContentDescriptors {
  ids?: number[];
  notes?: string;
}

// Системні вимоги
export interface SystemRequirements {
  minimum?: string;
  recommended?: string;
}

// Платформи
export interface Platforms {
  windows: boolean;
  mac: boolean;
  linux: boolean;
}

// Metacritic
export interface Metacritic {
  score?: number;
  url?: string;
}

export type Game = {
  appid: number;
  name: string;
  type?: string | null;
  parent_appid?: number | null;
  parent_name?: string | null;

  required_age?: number | null;
  is_free?: boolean | null;
  coming_soon?: boolean | null;
  release_date_string?: string | null;

  last_modified?: number | null;

  detailed_description?: string | null;
  about_the_game?: string | null;
  short_description?: string | null;
  supported_languages?: string | null;

  header_image?: string | null;
  capsule_image?: string | null;
  capsule_imagev5?: string | null;
  background?: string | null;
  background_raw?: string | null;
  website?: string | null;

  price_currency?: string | null;
  price_initial?: number | null;
  price_final?: number | null;
  price_discount?: number | null;
  price_initial_formatted?: string | null;
  price_final_formatted?: string | null;

  price_overview?: PriceOverview | null;

  developers?: string[] | null;
  publishers?: string[] | null;

  genres?: SteamGenre[] | string[] | null;
  categories?: GameCategory[] | string[] | null;

  screenshots?: Array<{
    id: number;
    path_thumbnail: string;
    path_full: string;
  }> | null;

  movies?: SteamMovie[] | null;

  supports_windows?: boolean | null;
  supports_mac?: boolean | null;
  supports_linux?: boolean | null;

  platforms?: {
    windows?: boolean;
    mac?: boolean;
    linux?: boolean;
  } | null;

  pc_requirements?: SystemRequirements | null;
  mac_requirements?: SystemRequirements | null;
  linux_requirements?: SystemRequirements | null;

  metacritic?: Metacritic | null;
  achievements?: SteamAchievements | null;

  release_date?: string | null;
  /** Дата виходу в Early Access (з games_full / steam_app_details) */
  ea_release_date?: string | null;
  /** Unix epoch (UTC) of scheduled release — populated by prices-railway from
   *  IStoreBrowseService/GetItems. Carries real hour when release_precision === 'date_full'. */
  release_ts_utc?: number | null;
  /** coming_soon_display flag from Steam: date_full | date_quarter | date_month | date_year | text_*. */
  release_precision?: string | null;
  /** Unix epoch of scheduled 1.0 release for games currently in Early Access. */
  release_from_ea_ts_utc?: number | null;

  support_info?: SupportInfo | null;

  ratings?: Record<string, SteamRating | undefined> | null;

  content_descriptors?: ContentDescriptors | null;

  packages?: number[] | null;
  package_groups?: SteamPackageGroup[] | null;

  dlc?: number[] | null;

  recommendations?: SteamRecommendations | null;
  recommendations_total?: number | null;

  raw_data?: Record<string, unknown> | null;

  // NOTE:
  // Залишається як є (сирий текст зі Steam details)
  reviews?: string | null;
};

export interface GameLanguage {
  elanguage?: number;
  language?: number; // з games_full view
  supported: boolean;
  full_audio: boolean;
  subtitles: boolean;
}

// Теги гри
export interface GameTag {
  tag_id: number;
  name: string;
  weight: number | null;
}

// Оцінки Steam з steam_app_reviews (з view games_full)
export interface SteamReview {
  steam_review_count: number | null;
  steam_review_percent_positive: number | null;
  steam_review_score: number | null;
  steam_review_score_label: string | null;
}

export interface GameWithRelations extends Game {
  app_last_modified?: number | null;

  genres_list?: string[] | null;
  categories_list?: string[] | null;

  tags?: GameTag[] | null;
  languages?: GameLanguage[] | null;

  // Плоскі поля з view games_full (steam_review_*)
  steam_review_count?: number | null;
  steam_review_percent_positive?: number | null;
  steam_review_score?: number | null;
  steam_review_score_label?: string | null;

  // Publisher clanids з view games_full (deprecated, використовуйте publishers_with_clanid)
  publisher_clanids?: number[] | null;

  // Publishers з view game_publishers (містить name та clanid)
  publishers_with_clanid?: Array<{ name: string; clanid: number }> | null;
}

// Re-export GameListItem from shared types
export type { GameListItem } from "@/types";
